import type { Store } from "@hobbyshopify/backend-client";
import { BaseController, type Rx, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import { claimShopPage, updateStore } from "../../../../core/ops/store-ops";
import { errorText } from "../../../../core/primitives/errors";
import { hasErrors } from "../../../../core/primitives/form-errors";
import {
  contactEmailError,
  hasMappableAddress,
  hostsShopPage,
  returnsPolicyError,
} from "../../../../core/primitives/shop-page";
import { shopSlugError, suggestShopSlug } from "../../../../core/primitives/shop-slug";
import { withScheme } from "../../../onboarding-website/onboarding-website-controller";
import type { HostedErrors, HostedField, WebsiteChoice } from "../../../onboarding-website/onboarding-website-model";
import { WebsiteSectionActions, WebsiteSectionState } from "./website-section-model";

export type WebsiteSectionDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "locationsRepository">;

export interface WebsiteSectionHost {
  readonly store: Rx<Store | null>;
}

/**
 * The web address Stripe checks, changeable after onboarding. A shop has one
 * address or the other, never both: claiming the hosted page clears
 * `websiteUrl` server-side, and saving a website leaves the old slug dormant.
 * The hosted branch enforces the same requirements as the onboarding step.
 */
export class WebsiteSectionController extends BaseController implements WebsiteSectionActions {
  readonly state = new WebsiteSectionState();
  #seededFrom: Store | null | undefined;

  constructor(
    subtree: SubtreeModel,
    readonly deps: WebsiteSectionDeps,
    readonly host: WebsiteSectionHost,
  ) {
    super();
    subtree.put(WebsiteSectionState, this.state);
    subtree.put(WebsiteSectionActions, this);
    this.sync(() => {
      const store = host.store.value;
      this.state.store.value = store;
      if (store && store !== this.#seededFrom) this.#seed(store);
    }, [host.store]);
    this.sync(() => {
      this.state.hasMappableLocation.value = hasMappableAddress(deps.locationsRepository.locations);
    }, [deps.locationsRepository]);
    this.sync(() => {
      this.state.slugProblem.value = this.state.mode.value === "hosted" ? shopSlugError(this.state.slug.value) : null;
    }, [this.state.mode, this.state.slug]);
  }

  setMode = (mode: WebsiteChoice) => {
    this.state.mode.value = mode;
  };

  setWebsiteUrl = (value: string) => {
    this.state.websiteUrl.value = value;
  };

  setSlug = (value: string) => {
    this.state.slug.value = value.toLowerCase();
  };

  setContactEmail = (value: string) => {
    this.state.contactEmail.value = value;
    this.#clearHostedError("contactEmail");
  };

  setReturnsPolicy = (value: string) => {
    this.state.returnsPolicy.value = value;
    this.#clearHostedError("returnsPolicy");
  };

  save = async () => {
    const store = this.host.store.value;
    if (!store || this.state.busy.value) return;
    this.state.error.value = null;
    this.state.saved.value = false;

    if (this.state.mode.value === "own") {
      const trimmed = this.state.websiteUrl.value.trim();
      if (!trimmed) {
        this.state.error.value = "Enter your website address, or switch to a hosted shop page.";
        return;
      }
      this.state.busy.value = true;
      const r = await updateStore({ ...this.deps, storeId: store.id, body: { websiteUrl: withScheme(trimmed) } });
      if (this.isDisposed) return;
      this.state.busy.value = false;
      if (!r.ok) {
        this.state.error.value = errorText(r.errors);
        return;
      }
      this.state.saved.value = true;
      return;
    }

    if (this.state.slugProblem.value) return;
    // Neither field may be cleared once the page exists: a blank used to write
    // `null` and quietly demote the page below `ShopPageOps.isPublishable`.
    const contactEmail = this.state.contactEmail.value;
    const returnsPolicy = this.state.returnsPolicy.value;
    const problems: HostedErrors = {};
    const complain = (field: HostedField, message: string | null) => {
      if (message) problems[field] = message;
    };
    complain("contactEmail", contactEmailError(contactEmail));
    complain("returnsPolicy", returnsPolicyError(returnsPolicy));
    this.state.hostedErrors.value = problems;
    if (hasErrors(problems)) return;

    this.state.busy.value = true;
    // Claim first: it clears `websiteUrl` server-side, so writing the page
    // content afterwards cannot be undone by it.
    const claimed = await claimShopPage({ ...this.deps, storeId: store.id, slug: this.state.slug.value });
    if (this.isDisposed) return;
    if (!claimed.ok) {
      this.state.busy.value = false;
      this.state.error.value = errorText(claimed.errors);
      return;
    }
    const updated = await updateStore({
      ...this.deps,
      storeId: store.id,
      body: { contactEmail: contactEmail.trim(), returnsPolicy: returnsPolicy.trim() },
    });
    if (this.isDisposed) return;
    this.state.busy.value = false;
    if (!updated.ok) {
      this.state.error.value = errorText(updated.errors);
      return;
    }
    this.state.saved.value = true;
  };

  #seed(store: Store): void {
    this.#seededFrom = store;
    this.state.mode.value = hostsShopPage(store) ? "hosted" : "own";
    this.state.websiteUrl.value = store.websiteUrl ?? "";
    this.state.slug.value = store.slug ?? suggestShopSlug(store.name ?? "");
    this.state.contactEmail.value = store.contactEmail ?? "";
    this.state.returnsPolicy.value = store.returnsPolicy ?? "";
    this.state.hostedErrors.value = {};
  }

  #clearHostedError(field: HostedField): void {
    if (this.state.hostedErrors.value[field]) this.state.hostedErrors.update(({ [field]: _, ...rest }) => rest);
  }
}
